import {useProjects} from '../../../providers/projects/useProjects.ts'
import {ProjectId} from '@package/shared'
import {Select, type SelectProps} from '@mantine/core'
import {useEffect, useMemo, useRef} from 'react'
import {ProjectBadge} from './badges/ProjectBadge.tsx'
import {IconCheck} from '@tabler/icons-react'

type Props = {
  value: ProjectId | undefined | null
  onChange: (projectId: ProjectId | null) => void
  allowArchivedSelection?: boolean
  disabled: boolean
} & Omit<SelectProps, 'value' | 'onChange' | 'disabled'>

export const ProjectSelect = ({
  value,
  onChange,
  allowArchivedSelection,
  required = false,
  disabled,
  ...props
}: Props) => {
  const {projects, loading} = useProjects()
  const preselected = useRef<boolean>(false)

  const sortedProjects = useMemo(() => {
    return projects.toSorted((a, b) => a.name.localeCompare(b.name))
  }, [projects])

  const activeProjects = useMemo(() => sortedProjects.filter((project) => !project.archivedTime), [sortedProjects])
  const archivedProjects = useMemo(() => sortedProjects.filter((project) => !!project.archivedTime), [sortedProjects])

  useEffect(() => {
    if (preselected.current) return

    if (value) {
      // deselect archived projectId
      const isArchivedProjectId = !activeProjects.map(({id}) => id).includes(value)
      if (!allowArchivedSelection && isArchivedProjectId) {
        onChange(null)
      }
    } else {
      // select the only active projectId
      if (activeProjects.length === 1) {
        onChange(activeProjects[0].id)
      }
    }

    preselected.current = true
  }, [activeProjects, allowArchivedSelection, onChange, value])

  return (
    <Select
      {...props}
      label={'Project'}
      data={[
        ...activeProjects.map((project) => ({
          value: project.id,
          label: project.name,
        })),
        ...(allowArchivedSelection
          ? archivedProjects.map((project) => ({
              value: project.id,
              label: project.name,
            }))
          : []),
      ]}
      renderOption={({option, checked}) => {
        return (
          <>
            {checked && <IconCheck stroke={1} />}
            <ProjectBadge projectId={option.value} />
          </>
        )
      }}
      leftSection={value ? <ProjectBadge projectId={value} compact /> : <div />}
      placeholder={'No project selected'}
      value={value || null}
      onChange={(value) => onChange(value ? ProjectId.parse(value) : null)}
      disabled={loading || disabled}
      searchable
      clearable
      allowDeselect
      required={required}
    />
  )
}
